import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import PaymentService from '../../services/paymentService';

const UpgradePage = () => {
  const { user, userProfile, refreshUserProfile } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('premium');
  const [cashfree, setCashfree] = useState(null);
  const cashfreeInitialized = useRef(false);
  const [sdkError, setSdkError] = useState(null);         // ← add this
  const [loadingMessage, setLoadingMessage] = useState('');


  // Initialize Cashfree SDK
  useEffect(() => {
    if (cashfreeInitialized.current) return;

    const initCashfree = async () => {
      try {
        // Load Cashfree SDK
        if (typeof window !== 'undefined' && !window.Cashfree) {
          const script = document.createElement('script');
          script.src = 'https://sdk.cashfree.com/js/v3/cashfree.js';
          script.async = true;

          // Error handler for script load failure
          script.onerror = () => {
            console.error('Failed to load Cashfree SDK');
            cashfreeInitialized.current = false;
            setSdkError('Payment gateway failed to load. Please refresh the page or try again later.');
          };

          script.onload = () => {
            try {
              const mode = import.meta.env.VITE_CASHFREE_ENV === 'production' ? 'production' : 'sandbox';
              const cf = window.Cashfree({
                mode: mode
              });
              setCashfree(cf);
              cashfreeInitialized.current = true;
            } catch (initError) {
              console.error('Failed to initialize Cashfree SDK:', initError);
              cashfreeInitialized.current = false;
              setSdkError('Payment gateway initialization failed. Please refresh the page.');
            }
          };

          document.head.appendChild(script);
        } else if (window.Cashfree) {
          try {
            const mode = import.meta.env.VITE_CASHFREE_ENV === 'production' ? 'production' : 'sandbox';
            const cf = window.Cashfree({
              mode: mode
            });
            setCashfree(cf);
            cashfreeInitialized.current = true;
          } catch (initError) {
            console.error('Failed to initialize Cashfree SDK:', initError);
            cashfreeInitialized.current = false;
            setSdkError('Payment gateway initialization failed. Please refresh the page.');
          }
        }
      } catch (error) {
        console.error('Failed to initialize Cashfree SDK:', error);
        cashfreeInitialized.current = false;
        setSdkError('Payment gateway failed to load. Please refresh the page.');
      }
    };

    initCashfree();
  }, []);

  // Redirect only enterprise users (not used in current system)
  useEffect(() => {
    if (!userProfile) return;

    // Only redirect enterprise tier (if you ever add it)
    if (userProfile.tier === 'enterprise') {
      navigate('/user-dashboard');
      return;
    }

    // Premium users CAN access this page to buy more credits
    // Free users CAN access to upgrade
  }, [userProfile, navigate]);

  const plans = [
    {
      id: 'premium',
      name: 'Premium Access',
      price: 325,
      originalPrice: 500,
      discountPercent: 35,
      currency: '₹',
      period: 'one-time',
      description: 'Get 2 resume analysis credits',
      features: [
        '2 comprehensive resume analyses',
        'Full skill gap analysis',
        'Personalized learning roadmap',
        'Interview prep questions',
        'Resume rewrite suggestions',
        'Bridge project recommendations',
        'Skill matching insights',
        'Career advancement recommendations',
        'Priority support'
      ],
      popular: true,
      ctaText: 'Upgrade to Premium'
    }
  ];

  const handlePayment = async (planId) => {
    if (!user) {
      navigate('/login', { state: { from: '/upgrade' } });
      return;
    }

    if (!cashfree) {
      alert('Payment gateway is loading. Please wait a moment and try again.');
      return;
    }

    setLoading(true);
    setSelectedPlan(planId);
    setLoadingMessage('Creating payment order...');

    try {
      const plan = plans.find(p => p.id === planId);
      if (!plan) {
        throw new Error('Invalid plan selected');
      }

      // Step 1: Create order via backend
      const orderData = await PaymentService.createOrder(
        user.id,
        planId,
        plan.price,
        user.email || '',
        userProfile?.full_name || user.email?.split('@')[0] || 'User',
        userProfile?.phone || '9999999999'
      );

      // Step 2: Open Cashfree checkout
      const checkoutOptions = {
        paymentSessionId: orderData.payment_session_id,
        redirectTarget: "_self" // Opens in same tab
      };
      setLoadingMessage('Opening payment page...');

      cashfree.checkout(checkoutOptions);

    } catch (error) {
      console.error('Payment error:', error);
      alert(error.message || 'Payment failed. Please try again.');
      setLoading(false);
      setLoadingMessage('');
    }
  };

  // Detect if this is a premium user topping up vs free user upgrading
  const isPremiumTopUp = userProfile?.tier === 'premium';

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/user-dashboard')}
            className="text-sm text-primary hover:underline flex items-center gap-2"
          >
            <Icon name="ArrowLeft" size={16} />
            Back to Dashboard
          </button>
          <div className="text-right">
            <h1 className="text-lg font-semibold text-foreground">
              {isPremiumTopUp ? 'Buy Credits' : 'Upgrade to Premium'}
            </h1>
            <p className="text-xs text-muted-foreground">
              {isPremiumTopUp ? 'Get 2 more scans' : 'Unlock all features'}
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary/10 mb-4">
            <Icon name={isPremiumTopUp ? "CreditCard" : "Crown"} size={40} className="text-primary" />
          </div>
          <h1 className="text-4xl font-bold text-foreground mb-4">
            {isPremiumTopUp ? 'Top Up Your Credits' : 'Unlock Your Career Potential'}
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {isPremiumTopUp
              ? 'Get 2 more credits to continue analyzing resumes and advancing your career.'
              : 'Get comprehensive resume analysis, personalized learning paths, and interview prep to land your dream job.'
            }
          </p>
        </div>

        {sdkError && (
          <div className="max-w-2xl mx-auto mb-8 p-4 border border-error/20 bg-error/10 rounded-lg text-sm text-error">
            {sdkError}
          </div>
        )}

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-1 gap-8 mb-12 max-w-2xl mx-auto">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative bg-card border-2 rounded-lg p-8 ${plan.popular
                ? 'border-primary shadow-lg scale-105'
                : 'border-border'
                }`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-primary text-primary-foreground px-4 py-1 rounded-full text-sm font-medium">
                    Most Popular
                  </span>
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-foreground mb-2">{plan.name}</h3>

                {/* Discount Badge */}
                {plan.discountPercent && (
                  <div className="mb-2">
                    <span className="inline-block bg-success text-success-foreground px-3 py-1 rounded-full text-sm font-bold animate-pulse">
                      🎉 {plan.discountPercent}% OFF - Limited Time!
                    </span>
                  </div>
                )}

                <div className="flex items-baseline justify-center gap-2 mb-2">
                  {/* Original Price Strikethrough */}
                  {plan.originalPrice && (
                    <span className="text-lg text-muted-foreground line-through">
                      {plan.currency}{plan.originalPrice}
                    </span>
                  )}

                  {/* Current Price */}
                  <span className="text-4xl font-bold text-foreground">
                    {plan.currency}{plan.price}
                  </span>
                  <span className="text-muted-foreground">
                    {plan.period === 'one-time' ? 'one-time' : '/month'}
                  </span>
                </div>

                {/* Savings Message */}
                {plan.originalPrice && (
                  <p className="text-sm text-success font-medium">
                    You save {plan.currency}{plan.originalPrice - plan.price}!
                  </p>
                )}

                <p className="text-sm text-muted-foreground mt-1">{plan.description}</p>
              </div>

              <ul className="space-y-4 mb-8">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Icon
                      name="Check"
                      size={20}
                      className="text-success shrink-0 mt-0.5"
                    />
                    <span className="text-foreground">{feature}</span>
                  </li>
                ))}
              </ul>

              <Button
                variant="default"
                size="lg"
                fullWidth
                onClick={() => handlePayment(plan.id)}
                loading={loading && selectedPlan === plan.id}
                iconName={isPremiumTopUp ? "CreditCard" : "Crown"}
                iconPosition="left"
                disabled={loading || !cashfree}
              >
                {isPremiumTopUp ? '💳 Buy 2 Credits' : plan.ctaText}
              </Button>
              {loading && selectedPlan === plan.id && loadingMessage && (
                <p className="mt-3 text-sm text-center text-muted-foreground">
                  {loadingMessage}
                </p>
              )}
            </div>
          ))}
        </div>

        {/* Trust Indicators */}
        <div className="bg-muted/50 border border-border rounded-lg p-8 mb-12">
          <h3 className="text-xl font-semibold text-foreground mb-6 text-center">
            Why Choose Us?
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 mb-3">
                <Icon name="Zap" size={24} className="text-primary" />
              </div>
              <h4 className="font-semibold text-foreground mb-2">Instant Access</h4>
              <p className="text-sm text-muted-foreground">
                Get immediate access to all features after payment
              </p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-success/10 mb-3">
                <Icon name="Shield" size={24} className="text-success" />
              </div>
              <h4 className="font-semibold text-foreground mb-2">Secure Payment</h4>
              <p className="text-sm text-muted-foreground">
                Your payment is processed securely through Cashfree
              </p>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="max-w-3xl mx-auto">
          <h3 className="text-2xl font-semibold text-foreground mb-6 text-center">
            Frequently Asked Questions
          </h3>
          <div className="space-y-4">
            <details className="bg-card border border-border rounded-lg p-4">
              <summary className="font-semibold text-foreground cursor-pointer">
                What payment methods do you accept?
              </summary>
              <p className="text-sm text-muted-foreground mt-2">
                We accept all major credit/debit cards, UPI, net banking, and wallets through Cashfree payment gateway.
              </p>
            </details>
            <details className="bg-card border border-border rounded-lg p-4">
              <summary className="font-semibold text-foreground cursor-pointer">
                What happens after payment?
              </summary>
              <p className="text-sm text-muted-foreground mt-2">
                After successful payment, credits will be added to your account immediately. You can start analyzing resumes right away.
              </p>
            </details>
          </div>
        </div>
      </main>
    </div>
  );
};

export default UpgradePage;